/**
 * Support generic functions without business logic attached.
 */

import { isNullish } from "@tinyurldev/tu-ui-vue";

import moment from "moment";
import Dinero from "dinero.js";
import * as currencies from "@dinero.js/currencies";

/**
 * Sleeps the JavaScript event loop by the given delay.
 *
 * @param {number} delay The sleep delay in milliseconds.
 *
 * @returns The promise that resolved once the delay is consumed.
 */
export const sleep = (delay) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, delay);
  });
};

/**
 * A wrapper for localStorage providing asynchronous versions of getItem and setItem.
 * @namespace asyncLocalStorage
 */
export const asyncLocalStorage = {
  /**
   * Asynchronously retrieves an item from localStorage.
   * @param {string} key The key under which the item is stored.
   * @returns {Promise<string|null>} A promise that resolves with the item's value, or null if the key doesn't exist.
   */
  getItem(key) {
    return new Promise((resolve, reject) => {
      try {
        const value = localStorage.getItem(key);
        resolve(value);
      } catch (error) {
        console.error("Error retrieving item from localStorage:", error);
        reject(error);
      }
    });
  },

  /**
   * Asynchronously sets an item in localStorage.
   * @param {string} key The key under which to store the item.
   * @param {any} value The value to store. This will be JSON-stringified.
   * @returns {Promise<void>} A promise that resolves when the item is successfully set.
   */
  setItem(key, value) {
    return new Promise((resolve, reject) => {
      try {
        localStorage.setItem(key, JSON.stringify(value));
        resolve();
      } catch (error) {
        console.error("Error setting item in localStorage:", error);
        reject(error);
      }
    });
  },
};

/**
 * A wrapper for sessionStorage providing asynchronous versions of getItem and setItem.
 * @namespace asyncSessionStorage
 */
export const asyncSessionStorage = {
  /**
   * Asynchronously retrieves an item from sessionStorage.
   * @param {string} key The key under which the item is stored.
   * @returns {Promise<string|null>} A promise that resolves with the item's value, or null if the key doesn't exist.
   */
  getItem(key) {
    return new Promise((resolve, reject) => {
      try {
        const value = sessionStorage.getItem(key);
        resolve(value);
      } catch (error) {
        console.error("Error retrieving item from sessionStorage:", error);
        reject(error);
      }
    });
  },

  /**
   * Asynchronously sets an item in sessionStorage.
   * @param {string} key The key under which to store the item.
   * @param {any} value The value to store. This will be JSON-stringified.
   * @returns {Promise<void>} A promise that resolves when the item is successfully set.
   */
  setItem(key, value) {
    return new Promise((resolve, reject) => {
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
        resolve();
      } catch (error) {
        console.error("Error setting item in sessionStorage:", error);
        reject(error);
      }
    });
  },
};

/**
 * Formats the given number with the given locale.
 *
 * @param value  The number value.
 * @param locale The optional locale.
 *
 * @returns The formatted number.
 */
export function formatNumberLocale(value, locale = "en") {
  return Intl.NumberFormat(locale).format(value);
}

/**
 * Formats a given date/time value to a localized string in "MMMM DD, YYYY" (e.g., "July 09, 2025") format.
 *
 * @param {string|Date|number} value - The date value to format. Can be a date string, Date object, or timestamp.
 * @returns {string} The formatted date string in the local timezone.
 */
export const toLongDate = (value, format = "MMMM DD, YYYY") => {
  return moment.utc(value).local().format(format);
};

/**
 * Formats a given date/time value to a localized string in "ll" (e.g., "Jan 1, 2025") format.
 *
 * @param {string|Date|number} value - The date/time value to format. Can be a date string, Date object, or timestamp.
 * @returns {string} The formatted date/time string in local time.
 */
export const toShortDate = (value) => {
  return moment.utc(value).local().format("ll");
};

/**
 * Formats a given date/time value to a localized string in "lll" (e.g., "Jan 1, 2025 1:00 AM") format.
 *
 * @param {string|Date|number} value - The date/time value to format. Can be a date string, Date object, or timestamp.
 * @returns {string} The formatted date/time string in local time.
 */
export const toShortDateTime = (value) => {
  return moment.utc(value).local().format("lll");
};

/**
 * Formats a given date/time value to a "lll" string kept in UTC, without
 * converting to the viewer's timezone.
 *
 * @param {string|Date|number} value - The date/time value to format. Can be a date string, Date object, or timestamp.
 * @returns {string} The formatted date/time string in UTC.
 */
export const toShortDateTimeUtc = (value) => {
  return moment.utc(value).format("lll");
};

/**
 * Returns a human-readable relative time string (e.g., "5 minutes ago") for the given date/time value.
 *
 * @param {string|number|Date} value - The date/time value to convert to a relative time string.
 * @returns {string} The relative time string representing how long ago the value occurred.
 */
export const toRelativeTime = (value) => {
  return moment.utc(value).local().fromNow();
};

/**
 * Formats a number into a compact, human-readable string (e.g., 1.2K, 3M) using the specified locale.
 *
 * @param {number} value - The number to format.
 * @param {string} [locale="en"] - Optional. The locale to use for formatting (defaults to "en").
 * @returns {string|number} The formatted number as a string, or 0 if the input value is falsy.
 */
export const toCompactNumber = (value, locale) => {
  locale = locale ?? "en";

  if (!value) return 0;
  return Intl.NumberFormat(locale, {
    notation: "compact",
    compactDisplay: "short",
  }).format(value);
};

/**
 * Truncates a long text.
 *
 * @param {string} text The long text.
 * @param {number} maxLength The allowed length before truncation.
 * @param {string} ellipsis The option type of truncation.
 * @returns {string} The now truncated long text.
 */
export const truncateText = (text, maxLength, ellipsis = "...") => {
  if (text.length <= maxLength) {
    return text;
  }

  return text.substring(0, maxLength - ellipsis.length) + ellipsis;
};

/**
 * @description Format currency based on currency code and locale
 *
 * @param {number} value - The amount of money to format.
 * @param {string} [currency="USD"] - The currency code (ISO 4217 format). Defaults to "USD".
 * @param {string} [locale="en"] - The locale string that determines the formatting style. Defaults to "en".
 * @param {boolean} [hasCents=true] - Indicates whether the currency has subunits (e.g., cents). Defaults to true.
 *
 * @returns {string} The formatted currency string.
 */
export const currencyFormat = (value, currency, locale, hasCents) => {
  currency = !isNullish(currency) ? currency : "USD";
  locale = !isNullish(locale) ? locale : "en";
  hasCents = !isNullish(hasCents) ? hasCents : true;

  let amount = parseFloat(value || 0);

  // convert float value into minor units
  if (amount % 1 !== 0) {
    const DEFAULT_EXPONENT = 2;
    const DECIMAL_MULTIPLIER = 10;
    const TARGET_EXPONENT = currencies[currency]?.exponent || DEFAULT_EXPONENT;

    amount = Math.round(amount * DECIMAL_MULTIPLIER ** TARGET_EXPONENT);
  }

  const formattedPrice = Dinero({ amount, currency }).setLocale(locale);

  if (hasCents || formattedPrice.hasSubUnits()) {
    return formattedPrice.toFormat();
  }

  return formattedPrice.toFormat("$0,0", "DOWN");
};

/**
 * Calculates the percentage of a value relative to a maximum value.
 *
 * @param {number} value - The value to calculate the percentage for.
 * @param {number} max - The maximum value. If 0, returns 100; if null, returns 0.
 * @param {boolean} [round=true] - Whether to round the result to the nearest integer. Defaults to true.
 *
 * @returns {number} The calculated percentage (0-100).
 */
export const getPercentage = (value, max, round = true) => {
  if (isNullish(max) || max === 0) {
    return max === 0 ? 100 : 0;
  }

  if (value === 0) {
    return 0;
  }

  const percent = (value / max) * 100;

  return round ? Math.round(percent) : percent;
};